import { createBrowserRouter, type RouteObject } from 'react-router';

import type { QueryClient } from '@tanstack/react-query';

import { coerceBoolean, isDefined, isNotEmpty, isOfType } from '@enterprise/core';
import type { AppNonIndexRoute, AppRoute } from '@enterprise/types';

import RedirectTo404 from '@/app/components/redirects/redirect-404';
import RedirectToDefault from '@/app/components/redirects/redirect-to-default';
import RedirectToError from '@/app/components/redirects/redirect-to-error';
import Root from '@/app/root';
import { useAppRoutes } from '@/app/routes/app-routes.hook';

const createRoute = (config: AppRoute, queryClient: QueryClient) => {
  const { action, breadcrumb, element, index, loader, meta, path } = config;
  const route: RouteObject = {
    action: isDefined(action) ? action(queryClient) : undefined,
    element,
    handle: {
      breadcrumb,
      meta,
    },
    index: coerceBoolean(index, false),
    loader: isDefined(loader) ? loader(queryClient) : undefined,
    path,
  };

  if (isOfType<AppNonIndexRoute>(config, 'children')) {
    const { children } = config;

    if (isDefined(children) && isNotEmpty(children)) {
      route.children = children.map((child) => {
        return createRoute(child, queryClient);
      });
    }
  }

  return route;
};

export const useCreateRouter = (queryClient: QueryClient) => {
  const { defaultLayout, fullPageLayout } = useAppRoutes();
  const children: RouteObject[] = [
    {
      element: <RedirectToDefault />,
      index: true,
    },
    createRoute(fullPageLayout, queryClient),
    createRoute(defaultLayout, queryClient),
    {
      element: <RedirectTo404 />,
      path: '*',
    },
  ];

  const siteRoutes: RouteObject[] = [
    {
      children,
      element: <Root />,
      errorElement: <RedirectToError />,
      path: '/',
    },
  ];

  return createBrowserRouter(siteRoutes);
};
